import { useState, useEffect, useRef, useMemo } from "react";
import { computeTreeLayout } from "../utils/treeLayout";
import styles from "../styles/theme";

export default function TreeView({ people, onSelectPerson }) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 900, height: 600 });
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDimensions({ width: rect.width, height: rect.height });
    }
  }, []);

  const layout = useMemo(
    () => computeTreeLayout(people, Math.max(dimensions.width / zoom, 900)),
    [people, dimensions.width, zoom],
  );

  const nodeMap = useMemo(() => {
    const m = {};
    layout.nodes.forEach((n) => (m[n.id] = n));
    return m;
  }, [layout]);

  // Compute content bounds for auto-fit
  const bounds = useMemo(() => {
    if (layout.nodes.length <= 1)
      return { minX: 0, maxX: 900, minY: 0, maxY: 400 };
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    layout.nodes.forEach((n) => {
      minX = Math.min(minX, n.x - n.w / 2);
      maxX = Math.max(maxX, n.x + n.w / 2);
      minY = Math.min(minY, n.y);
      maxY = Math.max(maxY, n.y + n.h);
    });
    return { minX, maxX, minY, maxY };
  }, [layout]);

  const _svgWidth = Math.max(bounds.maxX - bounds.minX + 200, dimensions.width); // eslint-disable-line no-unused-vars
  // eslint-disable-next-line no-unused-vars
  const _svgHeight = Math.max(
    bounds.maxY - bounds.minY + 100,
    dimensions.height,
  );

  const handleMouseDown = (e) => {
    if (e.target.tagName === "rect" || e.target.tagName === "text") return;
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setDragging(false);

  if (people.length === 0) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}>🌳</div>
        <h2 style={styles.emptyTitle}>Your Tree is Empty</h2>
        <p style={styles.emptyText}>
          Add people to see your relationship tree grow. Each person will appear
          under their category branch.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={styles.treeContainer}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Zoom Controls */}
      <div style={styles.zoomControls}>
        <button
          style={styles.zoomBtn}
          onClick={() => setZoom((z) => Math.min(z + 0.15, 2))}
        >
          +
        </button>
        <span style={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
        <button
          style={styles.zoomBtn}
          onClick={() => setZoom((z) => Math.max(z - 0.15, 0.3))}
        >
          −
        </button>
        <button
          style={styles.zoomBtn}
          onClick={() => {
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
        >
          ⟲
        </button>
      </div>

      <svg
        width={dimensions.width}
        height={dimensions.height}
        style={{ cursor: dragging ? "grabbing" : "grab" }}
      >
        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Edges */}
          {layout.edges.map((edge, i) => {
            const from = nodeMap[edge.from];
            const to = nodeMap[edge.to];
            if (!from || !to) return null;
            const x1 = from.x;
            const y1 = from.y + from.h;
            const x2 = to.x;
            const y2 = to.y;
            const midY = (y1 + y2) / 2;
            return (
              <path
                key={i}
                d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
                fill="none"
                stroke="#3a3a3e"
                strokeWidth={2}
                opacity={0.6}
              />
            );
          })}

          {/* Nodes */}
          {layout.nodes.map((node) => {
            const isRoot = node.type === "root";
            const isCat = node.type === "category";
            const isPerson = node.type === "person";
            const fillColor = isRoot
              ? "#C2785C"
              : isCat
                ? node.color
                : node.color + "33";
            const strokeColor = isRoot
              ? "#C2785C"
              : isCat
                ? node.color
                : node.color;
            const hasPhoto = isPerson && node.person.profilePicture;

            return (
              <g
                key={node.id}
                style={{ cursor: isPerson ? "pointer" : "default" }}
                onClick={() => isPerson && onSelectPerson(node.person)}
              >
                <rect
                  x={node.x - node.w / 2}
                  y={node.y}
                  width={node.w}
                  height={node.h}
                  rx={isRoot ? node.h / 2 : 12}
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth={isPerson ? 1.5 : 0}
                  opacity={isCat ? 0.9 : 1}
                />
                {hasPhoto && (
                  <>
                    <defs>
                      <clipPath id={`clip-${node.id}`}>
                        <circle
                          cx={node.x - node.w / 2 + 22}
                          cy={node.y + node.h / 2}
                          r={14}
                        />
                      </clipPath>
                    </defs>
                    <image
                      href={node.person.profilePicture}
                      x={node.x - node.w / 2 + 8}
                      y={node.y + node.h / 2 - 14}
                      width={28}
                      height={28}
                      clipPath={`url(#clip-${node.id})`}
                      preserveAspectRatio="xMidYMid slice"
                    />
                    <circle
                      cx={node.x - node.w / 2 + 22}
                      cy={node.y + node.h / 2}
                      r={14}
                      fill="none"
                      stroke={node.color}
                      strokeWidth={1.5}
                    />
                  </>
                )}
                <text
                  x={hasPhoto ? node.x + 10 : node.x}
                  y={node.y + node.h / 2 + (isCat ? -4 : -5)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={isRoot || isCat ? "#fff" : "#e8e4df"}
                  fontSize={isPerson ? 12 : 14}
                  fontWeight={isPerson ? 500 : 700}
                  fontFamily="'DM Sans', sans-serif"
                >
                  {node.label.length > (hasPhoto ? 14 : 18)
                    ? node.label.slice(0, hasPhoto ? 12 : 16) + "…"
                    : node.label}
                </text>
                {isCat && (
                  <text
                    x={node.x}
                    y={node.y + node.h / 2 + 14}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.7)"
                    fontSize={11}
                    fontFamily="'DM Sans', sans-serif"
                  >
                    {node.count} {node.count === 1 ? "person" : "people"}
                  </text>
                )}
                {isPerson && node.person.relationship && (
                  <text
                    x={hasPhoto ? node.x + 10 : node.x}
                    y={node.y + node.h / 2 + 8}
                    textAnchor="middle"
                    fill="#9a9a9e"
                    fontSize={10}
                    fontFamily="'DM Sans', sans-serif"
                  >
                    {node.person.relationship.length > 22
                      ? node.person.relationship.slice(0, 20) + "…"
                      : node.person.relationship}
                  </text>
                )}
                {isPerson && node.person.dateMet && (
                  <text
                    x={hasPhoto ? node.x + 10 : node.x}
                    y={node.y + node.h / 2 + 21}
                    textAnchor="middle"
                    fill="#7a7a7e"
                    fontSize={9}
                    fontFamily="'DM Sans', sans-serif"
                  >
                    Met{" "}
                    {new Date(
                      node.person.dateMet + "T00:00:00",
                    ).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </text>
                )}
                {isPerson && node.person.intimatePartner && (
                  <text
                    x={node.x + node.w / 2 - 14}
                    y={node.y + 14}
                    textAnchor="middle"
                    fontSize={11}
                  >
                    💜
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── PEOPLE LIST VIEW ────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
